import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { ScrollRegionUtil } from '../../../utils/scroll-region-util';

import type { ScrollRegionHandle } from './ScrollRegionContext';
import { ScrollRegionContext } from './ScrollRegionContext';

export type ScrollRegionProps = {
  axis?: 'both' | 'horizontal' | 'vertical';
  children?: React.ReactNode;
  /**
   * Sits the measuring out while something else is moving the layout - a gesture, a transition -
   * which would otherwise be measured on every frame of it. Pair it with `onSettled`.
   */
  isPaused?: () => boolean;
  maxHeight?: string;
  name?: string;
  /** Takes the measurement that matters once whatever `isPaused` reported has finished. */
  onSettled?: (callback: () => void) => () => void;
  /**
   * How much to keep clear at the top when something is scrolled into view here - so a pinned
   * item lands below the edge rather than against it.
   *
   * **It only lands if there is a viewport of content beneath the target.** Scrolling is clamped
   * at the end of the box, so an item near the bottom stops short of this offset.
   */
  pinOffset?: string;
  /**
   * Makes this a size container, so what is inside can be sized against it - `100cqh` for "as
   * tall as the region", rather than the viewport less whatever sits above it.
   */
  sizeContainer?: boolean;
  /** A fraction of the region's height, so the trigger point scales with the box. */
  threshold?: number;
};

/**
 * A box that scrolls its own overflow, and tells what is inside it where that scroll has got to.
 *
 * **It is the scrollport, so it must be the element with the overflow** - a column that holds one
 * gives its own scrolling up (`ResizableColumnsItem overflow="visible"`). That is what lets the
 * offsets, the container units and the listeners live on one element instead of being threaded to
 * whatever happens to be scrolling.
 */
export const ScrollRegion = forwardRef<ScrollRegionHandle, ScrollRegionProps>(
  (
    {
      axis = 'vertical',
      children,
      isPaused,
      maxHeight,
      name,
      onSettled,
      pinOffset,
      sizeContainer = false,
      threshold = 0.25,
    },
    handleRef
  ) => {
    const regionRef = useRef<HTMLDivElement | null>(null);
    /*
      State rather than a ref, so the measuring below starts when the footer arrives - a ref would
      be read once by an effect that had already run, and a composer mounted later never measured.
      Nothing is returned from the callback: React 19 reads a returned value as a cleanup function.
    */
    const [footer, setFooter] = useState<HTMLElement | null>(null);
    const footerRef = useCallback((node: HTMLElement | null) => {
      setFooter(node);
    }, []);
    const [isAwayFromBottom, setIsAwayFromBottom] = useState(false);

    const subscribers = useRef(new Set<() => void>());

    /*
      Held in a ref so the listeners below are attached once: a caller passing either of these
      inline would otherwise tear them down and rebuild them on every render.
    */
    const callbacks = useRef({ isPaused, onSettled });

    useEffect(() => {
      callbacks.current = { isPaused, onSettled };
    });

    const onMeasure = useCallback((callback: () => void) => {
      subscribers.current.add(callback);

      return () => {
        subscribers.current.delete(callback);
      };
    }, []);

    useEffect(() => {
      const region = regionRef.current;

      if (!region) return;

      const measure = () => {
        if (callbacks.current.isPaused?.()) return;

        setIsAwayFromBottom(
          ScrollRegionUtil.isAwayFromBottom({ scroller: region, threshold })
        );

        /*
          Iterated live, which is safe for a subscriber that unsubscribes itself - a deleted entry
          is simply not visited.
        */
        for (const callback of subscribers.current) callback();
      };

      measure();

      /*
        On the box, not `window`: a scroll inside an element does not reach the window's scroll
        event. `resize` stays on the window, which is what fires when the window changes size.
      */
      region.addEventListener('scroll', measure, { passive: true });
      window.addEventListener('resize', measure);

      /*
        The content growing is the other way to end up away from the end, and it fires no scroll
        event: appending leaves the reader where they were with more below them.
      */
      const resizeObserver = new ResizeObserver(measure);
      const content = region.firstElementChild;

      if (content) resizeObserver.observe(content);

      const unsubscribeFromSettled = callbacks.current.onSettled?.(measure);

      return () => {
        region.removeEventListener('scroll', measure);
        window.removeEventListener('resize', measure);
        resizeObserver.disconnect();
        unsubscribeFromSettled?.();
      };
    }, [threshold]);

    /*
      **The docked footer covers the end of the box**, so the region holds that much clear of
      anything scrolled to the end - without it a target lands underneath whatever is docked there.

      **The band, not the box**: measured from the footer's top to the region's own bottom edge,
      because a dock is usually lifted off that edge and the gap it leaves is covered too - its
      height alone left a target sitting under exactly that much of it.

      Measured rather than declared because it changes while it is read: a composer grows as a
      prompt wraps.
    */
    useEffect(() => {
      const region = regionRef.current;

      if (!region || !footer) return;

      const publish = () => {
        if (callbacks.current.isPaused?.()) return;

        const covered =
          region.getBoundingClientRect().bottom -
          footer.getBoundingClientRect().top;

        region.style.scrollPaddingBottom = `${Math.max(covered, 0)}px`;
      };

      publish();

      const resizeObserver = new ResizeObserver(publish);

      resizeObserver.observe(footer);

      const unsubscribeFromSettled = callbacks.current.onSettled?.(publish);

      return () => {
        resizeObserver.disconnect();
        unsubscribeFromSettled?.();
        region.style.removeProperty('scroll-padding-bottom');
      };
    }, [footer]);

    /*
      Reduced motion is checked by hand in both of these: it softens the CSS `scroll-behavior`
      property, but a `behavior` passed to `scrollTo` is a direct instruction and is honoured as
      given.
    */
    const scrollTo = useCallback(
      ({ instant, top }: { instant: boolean; top: number }) => {
        const region = regionRef.current;

        if (!region) return;

        const prefersReducedMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

        region.scrollTo({
          behavior: instant || prefersReducedMotion ? 'auto' : 'smooth',
          top,
        });
      },
      []
    );

    /*
      **Callers must not be holding focus when they call this.** A newly focused element is
      scrolled into view by the browser, which abandons a smooth scroll already in flight - so a
      button that starts one has to blur itself first.
    */
    const scrollToBottom = useCallback(
      ({ instant = false } = {}) => {
        scrollTo({ instant, top: regionRef.current?.scrollHeight ?? 0 });
      },
      [scrollTo]
    );

    const scrollToTop = useCallback(
      ({ instant = false } = {}) => {
        scrollTo({ instant, top: 0 });
      },
      [scrollTo]
    );

    useImperativeHandle(handleRef, () => ({ scrollToBottom, scrollToTop }), [
      scrollToBottom,
      scrollToTop,
    ]);

    const value = useMemo(
      () => ({
        footerRef,
        isAwayFromBottom,
        onMeasure,
        regionRef,
        scrollToBottom,
        scrollToTop,
      }),
      [footerRef, isAwayFromBottom, onMeasure, scrollToBottom, scrollToTop]
    );

    return (
      <ScrollRegionContext.Provider value={value}>
        <div
          className={AttrsHelper.formatClassname(
            'scroll-region',
            axis === 'both' && 'scroll-region-both-axes',
            axis === 'horizontal' && 'scroll-region-horizontal',
            maxHeight && 'scroll-region-capped',
            sizeContainer && 'size-container'
          )}
          data-test={name}
          ref={regionRef}
          style={{
            maxHeight: AttrsHelper.sanitizeCssValue(maxHeight),
            scrollPaddingTop: AttrsHelper.sanitizeCssValue(pinOffset),
          }}
        >
          {children}
        </div>
      </ScrollRegionContext.Provider>
    );
  }
);
